import { SelectionModel } from "@angular/cdk/collections";
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { Select } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { first, mergeMap, takeUntil } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { QueryStrings } from "src/app/models/emitter-events.models";
import { MirRule, VIEW_CONTROLS } from "src/app/models/mir-rule.model";
import { RulesService } from "src/app/services/rules/rules.service";
import { ZonesService } from "src/app/services/zones.service";
import { AssetsTableClickEvent, RestoreAssetsPageContext } from "./tables/assets-table/assets-table.component";
import { RestorePageContext, RulesTableClickEvent } from "./tables/rules-table/rules-table.component";
@Component({
	selector: "app-mir-rules",
	templateUrl: "./mir-rules.component.html",
	styleUrls: ["./mir-rules.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class MirRulesComponent implements OnInit, OnDestroy {
	constructor(private rulesService: RulesService, private zoneService: ZonesService) {}
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild("mirRules", { static: true }) mirRulesContainer: ElementRef;
	@Select(AppState.getSelectedCompanyId)
	selectedCompanyId$: Observable<string>;

	private onDestroy$ = new Subject<void>();

	sortActive = "";
	sortDirection = "desc";
	pageIndex = 0;
	pageSize = 10;
	pageSizeOptions = [10, 25, 50, 75, 100];
	selectionModel: SelectionModel<any>;
	restoreRulesPageContext: RestorePageContext;
	restoreAssetsPageContext: RestoreAssetsPageContext;

	sort: MatSort;

	previousControl = VIEW_CONTROLS.VIEW_RULE;

	view = "rules";

	currentQueryStrings: QueryStrings = {
		page: "1",
		perPage: this.pageSize.toString(),
		sort: "startTime",
		sortDir: "desc",
	};

	clickedRule: MirRule | null = null;

	controls = [
		{
			icon: "add_circle_outline",
			headerText: "create a rule",
			detailText: "create a rule description",
			value: VIEW_CONTROLS.CREATE_RULE,
			isSelected: false,
		},
		{
			icon: "lists",
			headerText: "view list of rules",
			detailText: "view list of rules description",
			value: VIEW_CONTROLS.VIEW_RULE,
			isSelected: true,
		},
		{
			icon: "local_shipping",
			headerText: "view list of assets",
			detailText: "view list of assets description",
			value: VIEW_CONTROLS.VIEW_ASSET,
			isSelected: false,
		},
	];

	ngOnInit(): void {
		this.showWizardFromEditButton();

		this.selectedCompanyId$.pipe(takeUntil(this.onDestroy$)).subscribe(this.abortWizard.bind(this));
	}

	rulesWereDeleted() {
		this.clickedRule = null;
	}
	controlBoxClick(value: string, rule: MirRule = null) {
		this.clickedRule = rule;

		if (value === VIEW_CONTROLS.CREATE_RULE) {
			this.rulesService.ruleToPatch$.next(null);
		}

		if (value === VIEW_CONTROLS.VIEW_RULE) {
			this.view = "rules";
			this.controls.forEach(c => {
				if (c.value === VIEW_CONTROLS.VIEW_RULE) {
					c.isSelected = true;
				} else {
					c.isSelected = false;
				}
			});
			this.previousControl = value;
		} else if (value === VIEW_CONTROLS.VIEW_ASSET) {
			this.view = "assets";

			this.controls.forEach(c => {
				if (c.value === VIEW_CONTROLS.VIEW_ASSET) {
					c.isSelected = true;
				} else {
					c.isSelected = false;
				}
			});
			this.previousControl = value;
		} else {
			this.view = "wizard";
			this.controls.forEach(c => {
				if (c.value === VIEW_CONTROLS.CREATE_RULE) {
					c.isSelected = true;
				} else {
					c.isSelected = false;
				}
			});
		}
	}

	ngOnDestroy() {
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	onWizardCancelled() {
		this.controlBoxClick(this.previousControl);
		this.restoreFocusToControlBox();
	}

	onSaveCompleted() {
		this.rulesService.savedRule$.pipe(first()).subscribe(rule => {
			const _rule = this.rulesService.formatRuleForDisplay(rule);
			this.controlBoxClick(VIEW_CONTROLS.VIEW_RULE, _rule);
			this.restoreFocusToControlBox();
		});
	}
	ruleRowClicked(event: RulesTableClickEvent) {
		// Create a new object reference to trigger ngOnChanges in child component
		this.rulesService.ruleToPatch$.next(null);
		this.clickedRule = { ...event.rule };
		this.restoreRulesPageContext = event.context;
	}

	ruleSummaryCloseClicked() {
		this.clickedRule = null;
	}

	onClearRuleClicked() {
		this.clickedRule = null;
	}
	assetRowClicked(event: AssetsTableClickEvent) {
		const { asset } = event;

		if (!asset.ruleId) {
			this.clickedRule = null;
			return;
		}
		this.selectedCompanyId$
			.pipe(
				mergeMap(companyId => {
					// we want to make a new fetch from the API because we want the most updated info in case a user assigns rules to assets
					// i.e. prevents a bug in https://zonarsystems.atlassian.net/browse/EVIR2-6397 where assigned assets were being reset to 0 because we were not sending the most recent rule info
					const obs$ = this.rulesService.getRuleById(asset.ruleId, companyId);
					return obs$;
				}),
				takeUntil(this.onDestroy$),
			)
			.subscribe(
				rule => {
					if (rule) {
						this.clickedRule = this.rulesService.formatRuleForDisplay(rule);
					}
				},
				_err => {
					this.clickedRule = null;
					console.log(_err);
				},
			);
	}

	abortWizard() {
		if (this.view === "wizard") {
			this.clickedRule = null;
			this.controlBoxClick(VIEW_CONTROLS.VIEW_RULE, null);
			this.restoreFocusToControlBox();
		}
	}

	private restoreFocusToControlBox() {
		setTimeout(() => {
			const el = this.mirRulesContainer?.nativeElement?.querySelector(".control-box.is-selected") as HTMLElement;
			if (el) {
				el.focus();
			}
		}, 0);
	}

	selectedRows(rows: MirRule[]) {
		if (rows.length === 1) {
			// Create a new object reference to trigger ngOnChanges in child component
			this.clickedRule = { ...rows[0] };
		} else {
			this.clickedRule = null;
		}
	}

	// listens to a shared Subject between rules summary and this container to determine if the wizard should be showed if someone clicked Edit on Rules Summary
	showWizardFromEditButton() {
		this.rulesService.confirmEditRule$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
			this.previousControl = this.view === "rules" ? VIEW_CONTROLS.VIEW_RULE : VIEW_CONTROLS.VIEW_ASSET;
			this.view = "wizard";

			this.controls.forEach(c => {
				if (c.value === VIEW_CONTROLS.CREATE_RULE) {
					c.isSelected = true;
				} else {
					c.isSelected = false;
				}
			});
		});
	}
}
